import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Questions to the client, answered from a phone without signing in.
 * The link is the credential; its token is hashed before it is stored.
 */
export const CLIENT_QUESTION_KINDS = [
    'missing_receipt',
    'which_job',
    'what_was_this',
    'other',
];
export const clientQuestionKindSchema = z.enum(CLIENT_QUESTION_KINDS);
export const CLIENT_QUESTION_KIND_LABELS = {
    missing_receipt: 'Missing receipt',
    which_job: 'Which job was this for?',
    what_was_this: 'What was this?',
    other: 'A question',
};
/** The default wording each kind starts with; the asker can change it. */
export const CLIENT_QUESTION_DEFAULT_PROMPTS = {
    missing_receipt: 'We have no receipt for this. Could you photograph it, or tell us what it was for? Without it the tax cannot be claimed.',
    which_job: 'Which job or customer was this for?',
    what_was_this: 'What was this payment for?',
    other: '',
};
export const CLIENT_QUESTION_STATUSES = ['open', 'answered', 'withdrawn', 'expired'];
export const clientQuestionStatusSchema = z.enum(CLIENT_QUESTION_STATUSES);
export const clientQuestionSchema = z.object({
    id: uuidSchema,
    kind: clientQuestionKindSchema,
    status: clientQuestionStatusSchema,
    prompt: z.string(),
    bankTransactionId: uuidSchema.nullable(),
    transactionId: uuidSchema.nullable(),
    /** "2026-10-12 · EMCO · $118.45", for the list. */
    about: z.string().nullable(),
    askedAt: isoDateTimeSchema,
    askedByName: z.string().nullable(),
    expiresAt: isoDateTimeSchema,
    answerText: z.string().nullable(),
    answerDocumentId: uuidSchema.nullable(),
    answeredName: z.string().nullable(),
    answeredAt: isoDateTimeSchema.nullable(),
});
export const askClientQuestionRequestSchema = z.object({
    kind: clientQuestionKindSchema,
    prompt: z.string().trim().min(3).max(1000),
    bankTransactionId: uuidSchema.nullable().optional(),
    transactionId: uuidSchema.nullable().optional(),
});
/** The question as created: its link is shown once and never stored. */
export const askedClientQuestionSchema = z.object({
    question: clientQuestionSchema,
    link: z.url(),
});
/** What the client sees when they open the link. */
export const clientQuestionPreviewSchema = z.object({
    businessName: z.string(),
    kind: clientQuestionKindSchema,
    prompt: z.string(),
    status: clientQuestionStatusSchema,
    expiresAt: isoDateTimeSchema,
    about: z
        .object({
        date: isoDateSchema,
        description: z.string().nullable(),
        amountMinor: z.string(),
        direction: z.enum(['in', 'out']),
        account: z.string().nullable(),
    })
        .nullable(),
    answer: z
        .object({
        text: z.string().nullable(),
        answeredAt: isoDateTimeSchema,
        hasDocument: z.boolean(),
    })
        .nullable(),
});
export const answerClientQuestionRequestSchema = z.object({
    answerText: z.string().trim().min(1).max(4000),
    answeredName: z.string().trim().max(120).nullable().optional(),
});
export const ANSWER_UPLOAD_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/pdf',
];
export const answerUploadTicketRequestSchema = z.object({
    filename: z.string().trim().min(1).max(200),
    mimeType: z.enum(ANSWER_UPLOAD_MIME_TYPES),
    byteSize: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024),
});
export const answerUploadTicketSchema = z.object({
    uploadUrl: z.url(),
    token: z.string(),
    objectKey: z.string(),
});
export const fileClientAnswerRequestSchema = z.object({
    objectKey: z.string().min(1),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.enum(ANSWER_UPLOAD_MIME_TYPES),
    byteSize: z.number().int().positive(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    answerText: z.string().trim().max(4000).nullable().optional(),
    answeredName: z.string().trim().max(120).nullable().optional(),
});
//# sourceMappingURL=questions.js.map